import { UserDocumentService } from './userDocumentService';
import { UserDocument, AccountData } from '../types/userDocument';

export interface DataSyncResult {
  success: boolean;
  data?: {
    demo: AccountData;
    real: AccountData;
  };
  error?: string;
}

export class DataSynchronizationService {
  /**
   * Synchronize all user data from Firestore
   * This includes demo and real account data, balances, trades, orders, etc.
   */
  static async synchronizeAllUserData(userId: string): Promise<DataSyncResult> {
    try {
      
      // Get the complete user document from Firestore
      const userDocument = await UserDocumentService.getUserDocument(userId);
      
      if (!userDocument) {
        throw new Error('User document not found');
      }
      
      // Extract account data for both demo and real accounts
      const demoAccountData = userDocument.accounts?.demo;
      const realAccountData = userDocument.accounts?.real;
      
      if (!demoAccountData || !realAccountData) {
        throw new Error('Account data not found in user document');
      }
      
      //   closedTrades: demoAccountData.closedTrades.length,
      //   tradingHistory: demoAccountData.tradingHistory.length,
      //   balance: demoAccountData.accountSummary.balance.totalBalance,
      //   aiTrades: demoAccountData.closedTrades.filter(t => t.source === 'ai').length,
      //   manualTrades: demoAccountData.closedTrades.filter(t => t.source === 'manual').length
      // });
      
      //   closedTrades: realAccountData.closedTrades.length,
      //   tradingHistory: realAccountData.tradingHistory.length,
      //   balance: realAccountData.accountSummary.balance.totalBalance,
      //   aiTrades: realAccountData.closedTrades.filter(t => t.source === 'ai').length,
      //   manualTrades: realAccountData.closedTrades.filter(t => t.source === 'manual').length
      // });
      
      return {
        success: true,
        data: {
          demo: demoAccountData,
          real: realAccountData
        }
      };
      
    } catch (error: any) {
      console.error('❌ Data synchronization failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to synchronize user data'
      };
    }
  }
  
  /**
   * Get account-specific data for a particular account type
   */
  static async getAccountData(userId: string, accountType: 'demo' | 'real'): Promise<AccountData | null> {
    try {
      const userDocument = await UserDocumentService.getUserDocument(userId);
      
      if (!userDocument) {
        return null;
      }
      
      return userDocument.accounts?.[accountType] || null;
      
    } catch (error) {
      console.error(`Error getting ${accountType} account data:`, error);
      return null;
    }
  }
  
  /**
   * Get comprehensive trading statistics for both accounts
   */
  static async getTradingStatistics(userId: string): Promise<{
    demo: {
      totalTrades: number;
      aiTrades: number;
      manualTrades: number;
      winRate: number;
      totalPnL: number;
      openPositions: number;
    };
    real: {
      totalTrades: number;
      aiTrades: number;
      manualTrades: number;
      winRate: number;
      totalPnL: number;
      openPositions: number;
    };
  } | null> {
    try {
      const userDocument = await UserDocumentService.getUserDocument(userId);
      
      if (!userDocument) {
        return null;
      }
      
      const demoData = userDocument.accounts?.demo;
      const realData = userDocument.accounts?.real;
      
      if (!demoData || !realData) {
        return null;
      }
      
      const calculateStats = (accountData: AccountData) => {
        const closedTrades = accountData.closedTrades;
        const aiTrades = closedTrades.filter(t => t.source === 'ai');
        const manualTrades = closedTrades.filter(t => t.source === 'manual');
        const winningTrades = closedTrades.filter(t => t.realizedPnL > 0);
        const totalPnL = closedTrades.reduce((sum, t) => sum + t.realizedPnL, 0);
        
        return {
          totalTrades: closedTrades.length,
          aiTrades: aiTrades.length,
          manualTrades: manualTrades.length,
          winRate: closedTrades.length > 0 ? (winningTrades.length / closedTrades.length) * 100 : 0,
          totalPnL: totalPnL,
          openPositions: accountData.openPositions.length
        };
      };
      
      return {
        demo: calculateStats(demoData),
        real: calculateStats(realData)
      };
      
    } catch (error) {
      console.error('Error getting trading statistics:', error);
      return null;
    }
  }
  
  /**
   * Check if user has any active trading data
   */
  static async hasActiveTradingData(userId: string): Promise<boolean> {
    try {
      const userDocument = await UserDocumentService.getUserDocument(userId);
      
      if (!userDocument) {
        return false;
      }
      
      const demoData = userDocument.accounts?.demo;
      const realData = userDocument.accounts?.real;
      
      if (!demoData || !realData) {
        return false;
      }
      
      const hasDemoData = demoData.openPositions.length > 0 || 
                         demoData.closedTrades.length > 0 || 
                         demoData.tradingHistory.length > 0;
                         
      const hasRealData = realData.openPositions.length > 0 || 
                         realData.closedTrades.length > 0 || 
                         realData.tradingHistory.length > 0;
      
      return hasDemoData || hasRealData;
      
    } catch (error) {
      console.error('Error checking active trading data:', error);
      return false;
    }
  }
  
  /**
   * Get the last activity timestamp for both accounts
   */
  static async getLastActivityTimestamps(userId: string): Promise<{
    demo: Date | null;
    real: Date | null;
    global: Date | null;
  }> {
    try {
      const userDocument = await UserDocumentService.getUserDocument(userId);
      
      if (!userDocument) {
        return { demo: null, real: null, global: null };
      }
      
      const demoData = userDocument.accounts?.demo;
      const realData = userDocument.accounts?.real;
      
      return {
        demo: demoData?.accountMetadata?.lastActive || null,
        real: realData?.accountMetadata?.lastActive || null,
        global: userDocument.lastUpdated || null
      };
      
    } catch (error) {
      console.error('Error getting last activity timestamps:', error);
      return { demo: null, real: null, global: null };
    }
  }
  
  /**
   * Validate data integrity for both accounts
   */
  static async validateDataIntegrity(userId: string): Promise<{
    isValid: boolean;
    issues: string[];
  }> {
    try {
      const userDocument = await UserDocumentService.getUserDocument(userId);
      
      if (!userDocument) {
        return { isValid: false, issues: ['User document not found'] };
      }
      
      const issues: string[] = [];
      
      // Check demo account
      const demoData = userDocument.accounts?.demo;
      if (!demoData) {
        issues.push('Demo account data missing');
      } else {
        // Validate demo account data
        if (demoData.accountSummary.balance.totalBalance < 0) {
          issues.push('Demo account has negative balance');
        }
        
        // Check for orphaned positions (positions without corresponding trades)
        const positionIds = demoData.openPositions.map(p => p.id);
        const tradeIds = demoData.closedTrades.map(t => t.id);
        const orphanedPositions = positionIds.filter(id => !tradeIds.includes(id));
        if (orphanedPositions.length > 0) {
          issues.push(`Demo account has ${orphanedPositions.length} orphaned positions`);
        }
      }
      
      // Check real account
      const realData = userDocument.accounts?.real;
      if (!realData) {
        issues.push('Real account data missing');
      } else {
        // Validate real account data
        if (realData.accountSummary.balance.totalBalance < 0) {
          issues.push('Real account has negative balance');
        }
        
        // Check for orphaned positions
        const positionIds = realData.openPositions.map(p => p.id);
        const tradeIds = realData.closedTrades.map(t => t.id);
        const orphanedPositions = positionIds.filter(id => !tradeIds.includes(id));
        if (orphanedPositions.length > 0) {
          issues.push(`Real account has ${orphanedPositions.length} orphaned positions`);
        }
      }
      
      return {
        isValid: issues.length === 0,
        issues
      };
      
    } catch (error) {
      console.error('Error validating data integrity:', error);
      return { isValid: false, issues: ['Data validation failed'] };
    }
  }
}
